import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureFlags } from '@feature-flags/feature-flags.const';
import { injectMutation, MutationResult } from '@ngneat/query';
import { ContextService } from 'app/core/services/context.service';
import { DoorStatus, TaskStatus } from 'app/shared/enums';
import { ApiParams, BaseApiService, PaginationOptionsWithFilter } from 'app/shared/services';
import {
  ApiResponse,
  IDoor,
  IDoorsResponse,
  ISubstituteTrailerDto,
  ITaskCountByStatus,
  ITaskDetailsResponse,
  ITaskMessages,
  ITaskOrder,
  ITaskOverrideRequest,
  ITaskRequest,
  ITaskResponse,
  ITaskTrailers,
  ITaskTrailersFilter,
  ITaskTypes,
  PaginatedData,
} from 'app/shared/types';
import { OptionsToParams } from 'app/shared/utility';
import { environment } from 'environment/environment';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { CONTROLLERS } from './controllers';

@Injectable({ providedIn: 'root' })
export class TaskApiService extends BaseApiService<
  ApiResponse<PaginatedData<ITaskResponse[]>>,
  ApiResponse<PaginatedData<ITaskResponse[]>>,
  ITaskRequest
> {
  private mutation = injectMutation();
  private readonly contextService = inject(ContextService);

  constructor(public override http: HttpClient) {
    super(http, environment.yardOpsApiBaseUrl);
  }

  public getTasksTypes(): Observable<ITaskTypes[]> {
    return this.http.get<ApiResponse<ITaskTypes[]>>(`${this.api}/${this.controllerName()}/types`).pipe(
      map((tasksResponse) => tasksResponse.value),
      shareReplay(1),
    );
  }

  public override controllerName(): string {
    return CONTROLLERS.tasks;
  }

  private isFeatureFlagEnabled(): boolean {
    const flags = this.contextService.featureFlags.getValue();
    return !!flags?.[FeatureFlags.DockActivityUseDockGroupIdsEndpoint];
  }

  public getTasks(options?: PaginationOptionsWithFilter): Observable<ApiResponse<PaginatedData<ITaskResponse[]>>> {
    let params = new HttpParams();
    if (options) {
      params = OptionsToParams.processOptionsToParams(params, options);
    }
    params = this.mapParamsByTaskStatus(params, options);

    return this.http.get<ApiResponse<PaginatedData<ITaskResponse[]>>>(`${this.api}/${this.controllerName()}`, {
      params,
    });
  }

  public getDriverTasks(
    options?: PaginationOptionsWithFilter,
  ): Observable<ApiResponse<PaginatedData<ITaskResponse[]>>> {
    let params = new HttpParams();
    if (options) {
      params = OptionsToParams.processOptionsToParams(params, options);
    }
    params = this.mapParamsByTaskStatus(params, options);

    return this.http.get<ApiResponse<PaginatedData<ITaskResponse[]>>>(`${this.api}/api/driver-tasks`, { params });
  }

  public getTaskById(id: string): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.get<ApiResponse<ITaskDetailsResponse>>(`${this.api}/${this.controllerName()}/${id}`);
  }

  public deleteTask(id: string): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.delete<ApiResponse<ITaskDetailsResponse>>(`${this.api}/${this.controllerName()}/${id}`);
  }

  public updateTask(task: ITaskRequest): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.put<ApiResponse<ITaskDetailsResponse>>(`${this.api}/${this.controllerName()}/${task.id}`, {
      ...task,
    });
  }

  public createTask(task: ITaskRequest): Observable<ApiResponse<ITaskResponse>> {
    return this.http.post<ApiResponse<ITaskResponse>>(`${this.api}/${this.controllerName()}`, task);
  }

  public getShipmentNumbers(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.api}/api/shipment-numbers`);
  }

  public startTask(id: string): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.patch<ApiResponse<ITaskDetailsResponse>>(`${this.api}/api/tasks-status/${id}`, {
      id,
      status: TaskStatus.WORKING,
    });
  }

  public completeTask({
    id,
    currentLocationId,
    boxTemperature,
    weight,
    fuelAmount,
    captureId,
  }: {
    id: string;
    currentLocationId?: string;
    boxTemperature?: number | null;
    weight?: number | null;
    fuelAmount?: number | null;
    captureId?: string | null;
  }): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.patch<ApiResponse<ITaskDetailsResponse>>(`${this.api}/api/tasks-status/${id}`, {
      id,
      boxTemperature,
      weight,
      fuelAmount,
      currentLocationId,
      status: TaskStatus.COMPLETED,
      captureId,
    });
  }

  public pauseTask({
    id,
    pauseReasonCode,
  }: {
    id: string;
    pauseReasonCode: string;
  }): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.patch<ApiResponse<ITaskDetailsResponse>>(`${this.api}/api/tasks-status/${id}`, {
      id,
      pauseReasonCode,
      status: TaskStatus.PAUSED,
    });
  }

  public unpauseTask(id: string): Observable<ApiResponse<ITaskDetailsResponse>> {
    return this.http.patch<ApiResponse<ITaskDetailsResponse>>(`${this.api}/api/tasks-status/${id}`, {
      id,
      status: TaskStatus.PENDING,
    });
  }

  public overrideTask(id: string, params: ITaskOverrideRequest): Observable<ApiResponse<ITaskDetailsResponse>> {
    const { ...body } = params;
    return this.http.patch<ApiResponse<ITaskDetailsResponse>>(`${this.api}/api/tasks-status/${id}`, body);
  }

  public getTasksCountByStatus(options: {
    dateFrom: string | null;
    dateTo: string | null;
  }): Observable<ApiResponse<ITaskCountByStatus>> {
    const params = OptionsToParams.processOptionsToParams(new HttpParams(), options);
    return this.http.get<ApiResponse<ITaskCountByStatus>>(`${this.api}/${this.controllerName()}/counts`, { params });
  }

  public orderTasks(order: ITaskOrder): Observable<ApiResponse<ITaskOrder>> {
    return this.http.post<ApiResponse<ITaskOrder>>(`${this.api}/${this.controllerName()}/${order.taskId}/order`, order);
  }

  public getTrailerById(id: string): Observable<ApiResponse<ITaskTrailers>> {
    return this.http.get<ApiResponse<ITaskTrailers>>(`${this.api}/api/trailers/${id}`);
  }

  public getTrailers(options?: {
    scacCode?: string;
    taskType?: string | null;
  }): Observable<ApiResponse<ITaskTrailers[]>> {
    let params = new HttpParams();

    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) {
          params = params.append(key, value);
        }
      });
    }

    return this.http.get<ApiResponse<ITaskTrailers[]>>(`${this.api}/api/trailers/task-eligibility`, {
      params,
    });
  }

  public getTrailersForFilter(options?: {
    scacCode?: string;
    taskType?: string | null;
  }): Observable<ApiResponse<ITaskTrailersFilter[]>> {
    let params = new HttpParams();

    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) {
          params = params.append(key, value);
        }
      });
    }

    return this.http.get<ApiResponse<ITaskTrailers[]>>(`${this.api}/api/trailer-numbers-filter`, {
      params,
    });
  }

  public substituteTrailerMutation(): MutationResult<object, Error, ISubstituteTrailerDto, unknown> {
    return this.mutation({
      mutationFn: ({ id, ...dto }) =>
        this.http.patch(`${this.api}/${this.controllerName()}/${id}/trailers`, {
          ...dto,
        }),
    });
  }

  public getTaskDataExportCSV(options?: PaginationOptionsWithFilter): Observable<{
    blob: Blob | null;
    filename: string;
  }> {
    let params = new HttpParams();
    if (options) {
      params = OptionsToParams.processOptionsToParams(params, options);
    }
    return this.http
      .get(`${this.api}/${this.controllerName()}/export/csv`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers: {
          'content-type': 'text/csv',
        },
      })
      .pipe(
        map((res) => {
          const filenameString = res.headers.get('content-disposition')?.split(';')[1];
          const filename = filenameString?.split('"')[1].trim() ?? 'tasks.csv';
          return {
            blob: res.body,
            filename,
          };
        }),
      );
  }

  public getTaskMessages(
    taskId: string,
    options?: PaginationOptionsWithFilter,
  ): Observable<ApiResponse<ITaskMessages>> {
    let params = new HttpParams();
    if (options) {
      params = OptionsToParams.processOptionsToParams(params, options);
    }
    return this.http.get<ApiResponse<ITaskMessages>>(`${this.api}/${this.controllerName()}/${taskId}/messages`, {
      params,
    });
  }

  public sendTaskMessage(taskId: string, message: string, isSystemMessage = false): Observable<string> {
    return this.http.post<string>(`${this.api}/${this.controllerName()}/${taskId}/message`, {
      message,
      isSystem: isSystemMessage,
    });
  }

  public getDoors(
    options?: ApiParams<{
      doorStatuses?: DoorStatus[];
      dockGroupId?: string | null;
      dockGroupIds?: string | null;
    }>,
  ): Observable<ApiResponse<IDoorsResponse>> {
    let params = new HttpParams();
    params = OptionsToParams.processOptionsToParams(params, { ...options });

    // All doors is more than 1 or 0, out of service is 0
    if (options?.doorStatuses && options.doorStatuses.length === 1) {
      params = params.set('isOutOfService', false);
    }

    // Out of service was status, currently it is not, this should be future proof.
    if (options?.doorStatuses?.includes(DoorStatus.OutOfService)) {
      params = params.delete('doorStatuses', DoorStatus.OutOfService);
      params = params.set('isOutOfService', true);
    }

    if (this.isFeatureFlagEnabled()) {
      params = params.set('api-version', '2.0');
    }

    return this.http.get<ApiResponse<IDoorsResponse>>(`${this.api}/api/doors`, {
      params,
    });
  }

  public getDoorsCounts(
    options?: ApiParams<{
      dockGroupId?: string | null;
      dockGroupIds?: string | null;
    }>,
  ): Observable<ApiResponse<Record<string, number>>> {
    let params = new HttpParams();
    params = OptionsToParams.processOptionsToParams(params, { ...options });

    if (this.isFeatureFlagEnabled()) {
      params = params.set('api-version', '2.0');
    }

    return this.http.get<ApiResponse<Record<string, number>>>(`${this.api}/api/doors/counts`, {
      params,
    });
  }

  public updateDoor(params: Pick<IDoor, 'doorId' | 'doorName' | 'isOutOfService'>): Observable<ApiResponse<string>> {
    const { doorId, isOutOfService, doorName: name } = params;
    return this.http.patch<ApiResponse<string>>(`${this.api}/api/doors/${doorId}`, {
      isOutOfService,
      name,
    });
  }

  private mapParamsByTaskStatus(params: HttpParams, options?: PaginationOptionsWithFilter): HttpParams {
    switch (options?.taskStatuses) {
      case TaskStatus.COMPLETED:
        return params.set('taskStatuses', TaskStatus.COMPLETED);
      case TaskStatus.PENDING:
        return params.append('taskStatuses', TaskStatus.PAUSED);
      case TaskStatus.WORKING:
        return params.set('taskStatuses', TaskStatus.WORKING);
      default:
        return params.appendAll({
          taskStatuses: [TaskStatus.WORKING, TaskStatus.PAUSED, TaskStatus.PENDING],
        });
    }
  }
}
